import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { ChevronDown, ChevronUp, Edit, Upload, MessageSquare, Download, Percent, FileDown, CheckSquare, ArrowRight, X as XIcon } from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import OrderModificationModal from '../../components/wholesaler/OrderModificationModal';
import OrderLightEditModal from '../../components/wholesaler/OrderLightEditModal';
import InvoiceUploadModal from '../../components/wholesaler/InvoiceUploadModal';
import OrderDiscountModal from '../../components/wholesaler/OrderDiscountModal';
import OrderStatusProgression from '../../components/wholesaler/OrderStatusProgression';
import ModificationHistorySection from '../../components/order/ModificationHistorySection';
import LightEditAuditSection from '../../components/order/LightEditAuditSection';
import { wholesalerAPI, ordersAPI, API_URL } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import ExportSettingsModal from '../../components/wholesaler/ExportSettingsModal';
import useRangeSelect from '../../hooks/useRangeSelect';
import DragSelectOverlay from '../../components/common/DragSelectOverlay';
import FloatingActionBar from '../../components/common/FloatingActionBar';
import ConfirmModal from '../../components/common/ConfirmModal';
import ConfirmDeliveryDate from '../../components/order/ConfirmDeliveryDate';

// Status flow for forward transitions
const STATUS_FLOW = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];
const getNextStatus = (current) => {
  const idx = STATUS_FLOW.indexOf(current);
  return idx >= 0 && idx < STATUS_FLOW.length - 1 ? STATUS_FLOW[idx + 1] : null;
};

const WholesalerOrders = () => {
  const { t } = useTranslation('wholesaler');
  const { user } = useAuth();
  const subRole = user?.teamContext?.subRole || 'manager';
  // Sales reps can view orders for their accounts but cannot mutate status
  const canManageStatus = subRole !== 'sales_rep';

  // State variables
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState('all');
  const [businessFilter, setBusinessFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [businesses, setBusinesses] = useState([]);

  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [showLightEditModal, setShowLightEditModal] = useState(false);
  const [erpActive, setErpActive] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Export Modal State
  const [showExportModal, setShowExportModal] = useState(false);
  const [orderToExport, setOrderToExport] = useState(null);
  const [ordersToExport, setOrdersToExport] = useState(null);

  const [cancellationReason, setCancellationReason] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [commentText, setCommentText] = useState({});

  // Pagination state
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Status counts from API
  const [statusCounts, setStatusCounts] = useState({
    all: 0, pending: 0, confirmed: 0, preparing: 0,
    in_transit: 0, delivered: 0, cancelled: 0
  });

  // ── Bulk Selection via useRangeSelect ──
  const {
    selectedIds,
    setSelectedIds,
    clearSelection,
    dragProps,
    isDragging,
    dragRect,
    dragSelectedIds,
  } = useRangeSelect(filteredOrders);

  const [bulkProcessing, setBulkProcessing] = useState(false);

  // ── Confirm Modal State ──
  const [confirmModal, setConfirmModal] = useState({ open: false, type: null });
  const [bulkCancelReason, setBulkCancelReason] = useState('');

  // Securely Get Link & Open
  const handleViewInvoice = async (orderId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error(t('orders.alerts.notLoggedIn'));
        return;
      }

      const response = await axios.get(`${API_URL}/wholesalers/orders/${orderId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.url) {
        console.log('Opening invoice:', response.data.url);
        window.open(response.data.url, '_blank');
      } else {
        throw new Error('Server did not return a valid URL');
      }

    } catch (err) {
      console.error('Error viewing invoice:', err);
      const msg = err.response?.data?.message || err.message || 'Unknown error';
      toast.error(`${t('orders.alerts.invoiceLoadFailed')}: ${msg}`);
    }
  };

  useEffect(() => {
    fetchOrders();
    wholesalerAPI.getProfile()
      .then(res => setErpActive(res.data?.wholesaler?.erpIntegration?.isActive === true))
      .catch(() => {});
  }, []);

  useEffect(() => {
    applyFilters();
  }, [orders, statusFilter, businessFilter, dateFilter, searchTerm]);

  useEffect(() => {
    if (Array.isArray(orders) && orders.length > 0) {
      const uniqueBusinesses = [...new Map(
        orders.filter(o => o.businessId).map(o => [o.businessId._id, o.businessId])
      ).values()];
      setBusinesses(uniqueBusinesses);
    }
  }, [orders]);

  const loadMore = async () => {
    if (!hasMore || loadingMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchOrders(nextPage, true);
  };

  const fetchOrders = async (pageToFetch = 1, append = false) => {
    try {
      setLoading(!append);
      if (append) setLoadingMore(true);

      const response = await wholesalerAPI.getOrders({
        page: pageToFetch,
        limit: 20
      });

      const ordersList = response.data.data || response.data.orders || response.data || [];

      if (append) {
        setOrders(prev => {
          const existingIds = new Set(prev.map(o => o._id));
          const newOrders = ordersList.filter(o => !existingIds.has(o._id));
          return [...prev, ...newOrders];
        });
      } else {
        setOrders(ordersList);
        setPage(1);
      }

      if (response.data.pagination) {
        setPagination(response.data.pagination);
        setHasMore(response.data.pagination.page < response.data.pagination.pages);
      }

      if (response.data.statusCounts) {
        setStatusCounts(response.data.statusCounts);
      }
    } catch (err) {
      setError('Failed to fetch orders.');
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const applyFilters = () => {
    if (!Array.isArray(orders)) {
      setFilteredOrders([]);
      return;
    }

    let filtered = [...orders];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    if (businessFilter !== 'all') {
      filtered = filtered.filter(order => order.businessId?._id === businessFilter);
    }

    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.setHours(0, 0, 0, 0));

      filtered = filtered.filter(order => {
        const orderDate = new Date(order.createdAt);
        switch (dateFilter) {
          case 'today': return orderDate >= today;
          case 'week':
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return orderDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return orderDate >= monthAgo;
          default: return true;
        }
      });
    }

    if (searchTerm) {
      filtered = filtered.filter(order =>
        order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.businessId?.businessName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredOrders(filtered);
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setBusinessFilter('all');
    setDateFilter('all');
    setSearchTerm('');
  };

  const handleStatusUpdate = async (orderId, newStatus, payload) => {
    if (newStatus === 'cancelled') {
      const order = orders.find(o => o._id === orderId);
      setSelectedOrder(order);
      setShowCancelModal(true);
      return;
    }
    try {
      const body = payload ? { status: newStatus, ...payload } : newStatus;
      await wholesalerAPI.updateOrderStatus(orderId, body);
      toast.success(t('orders.alerts.statusUpdated'));
      fetchOrders();
    } catch (err) {
      toast.error(t('orders.alerts.statusFailed'));
      console.error(err);
    }
  };

  const handleConfirmCancellation = async () => {
    if (!cancellationReason.trim()) {
      toast.error(t('orders.alerts.provideReason'));
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API_URL}/wholesalers/orders/${selectedOrder._id}/status`, {
        status: 'cancelled',
        reason: cancellationReason
      }, { headers });

      toast.success(t('orders.alerts.statusUpdated'));
      setShowCancelModal(false);
      setCancellationReason('');
      setSelectedOrder(null);
      fetchOrders();
    } catch (err) {
      toast.error(t('orders.alerts.statusFailed'));
      console.error(err);
    }
  };

  const handleModifyOrder = (order) => { setSelectedOrder(order); setShowModifyModal(true); };
  const handleLightEditOrder = (order) => { setSelectedOrder(order); setShowLightEditModal(true); };
  const handleUploadInvoice = (order) => { setSelectedOrder(order); setShowInvoiceModal(true); };
  const handleApplyDiscount = (order) => { setSelectedOrder(order); setShowDiscountModal(true); };

  // Handle Export Click with Data Mapping
  const handleExportERP = (order) => {
    const orderWithDetails = {
      ...order,
      items: order.items.map(item => ({
        ...item,
        sku: item.productId?.sku || item.sku || '',
        category: item.productId?.category || item.category || '',
        brand: item.productId?.brand || item.brand || '',
      }))
    };

    setOrderToExport(orderWithDetails);
    setShowExportModal(true);
  };

  const handleDiscountApplied = (updatedOrder) => {
    if (updatedOrder && updatedOrder._id) {
      setOrders(prevOrders =>
        prevOrders.map(o => o._id === updatedOrder._id ? { ...o, discount: updatedOrder.discount, totals: updatedOrder.totals } : o)
      );
    } else {
      fetchOrders(page);
    }
    setShowDiscountModal(false);
    setSelectedOrder(null);
  };

  const handleSaveModifications = async (modifiedItems, reason) => {
    try {
      await wholesalerAPI.modifyOrderItems(selectedOrder._id, modifiedItems, reason || 'Order modified by wholesaler');
      toast.success(t('orders.alerts.itemsUpdated'));
      setShowModifyModal(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch (err) {
      toast.error(t('orders.alerts.itemsFailed'));
      console.error(err);
    }
  };

  const handleSaveLightEdit = async (payload) => {
    try {
      await wholesalerAPI.lightEditOrder(selectedOrder._id, payload);
      toast.success(t('orders.alerts.itemsUpdated'));
      setShowLightEditModal(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.message || t('orders.alerts.itemsFailed'));
      console.error(err);
    }
  };

  const handleInvoiceUpload = async (formData) => {
    try {
      await wholesalerAPI.uploadInvoice(selectedOrder._id, formData);
      toast.success(t('orders.alerts.invoiceUploaded'));
      setShowInvoiceModal(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch (err) {
      toast.error(t('orders.alerts.invoiceFailed'));
      console.error(err);
    }
  };

  const handleAddComment = async (orderId) => {
    const message = commentText[orderId];
    if (!message || !message.trim()) {
      toast.error(t('orders.alerts.commentRequired'));
      return;
    }
    try {
      await wholesalerAPI.addOrderComment(orderId, message);
      setCommentText({ ...commentText, [orderId]: '' });
      toast.success(t('orders.alerts.commentAdded'));
      fetchOrders();
    } catch (err) {
      toast.error(t('orders.alerts.commentFailed'));
      console.error(err);
    }
  };

  // ── Bulk Selection Handlers ──
  const bulkSelectedStatus = (() => {
    if (selectedIds.size === 0) return null;
    const statuses = new Set();
    for (const id of selectedIds) {
      const order = orders.find(o => o._id === id);
      if (order) statuses.add(order.status);
    }
    return statuses.size === 1 ? [...statuses][0] : null;
  })();

  // Track whether the homogeneous selection is of simple orders.
  const bulkSelectedIsSimple = (() => {
    if (selectedIds.size === 0) return false;
    for (const id of selectedIds) {
      const order = orders.find(o => o._id === id);
      if (order && order.isSimple) return true;
    }
    return false;
  })();

  // Simple-pending advances directly to delivered (=completed); full orders use the standard chain.
  const bulkNextStatus = bulkSelectedStatus
    ? (bulkSelectedIsSimple
        ? (bulkSelectedStatus === 'pending' ? 'delivered' : null)
        : getNextStatus(bulkSelectedStatus))
    : null;

  // Toggle enforces same-status AND same-isSimple-type so a selection is homogeneous.
  const toggleOrderSelection = (orderId, orderStatus) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        if (next.size > 0) {
          const existing = orders.find(o => next.has(o._id));
          const candidate = orders.find(o => o._id === orderId);
          if (existing && candidate) {
            if (existing.status !== orderStatus) return prev;
            if (!!existing.isSimple !== !!candidate.isSimple) return prev;
          }
        }
        next.add(orderId);
      }
      return next;
    });
  };

  const selectAllFiltered = () => {
    if (filteredOrders.length === 0) return;
    const seed = filteredOrders.find(o => selectedIds.has(o._id)) || filteredOrders[0];
    const targetStatus = seed.status;
    const targetIsSimple = !!seed.isSimple;
    const matching = filteredOrders.filter(o =>
      o.status === targetStatus && !!o.isSimple === targetIsSimple
    );
    setSelectedIds(new Set(matching.map(o => o._id)));
  };

  const handleBulkAdvance = async () => {
    if (!bulkSelectedStatus || !bulkNextStatus || selectedIds.size === 0) return;

    setConfirmModal({ open: true, type: 'advance' });
  };

  const confirmBulkAdvance = async () => {
    setConfirmModal({ open: false, type: null });

    try {
      setBulkProcessing(true);
      const res = await wholesalerAPI.bulkUpdateOrderStatus(
        [...selectedIds],
        bulkSelectedStatus,
        bulkNextStatus
      );
      const results = res.data.results;
      toast.success(t('orders.bulk.success', { updated: results.updated, failed: results.failed }) || `${results.updated} orders updated, ${results.failed} failed`);
      clearSelection();
      fetchOrders();
    } catch (err) {
      console.error('Bulk advance error:', err);
      toast.error(t('orders.bulk.error') || 'Failed to update orders');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkExportExcel = (selectedOrders) => {
    if (selectedOrders.length === 0) return;

    const enrichedOrders = selectedOrders.map(order => ({
      ...order,
      items: (order.items || []).map(item => ({
        ...item,
        sku: item.productId?.sku || item.sku || '',
        category: item.productId?.category || item.category || '',
        brand: item.productId?.brand || item.brand || '',
      }))
    }));

    setOrdersToExport(enrichedOrders);
    setOrderToExport(null);
    setShowExportModal(true);
  };

  const handleBulkCancel = () => {
    if (selectedIds.size === 0) return;
    setBulkCancelReason('');
    setConfirmModal({ open: true, type: 'bulkCancel' });
  };

  const confirmBulkCancel = async () => {
    if (!bulkCancelReason.trim()) return;

    setConfirmModal({ open: false, type: null });

    try {
      setBulkProcessing(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      let succeeded = 0;
      let failed = 0;

      for (const orderId of selectedIds) {
        try {
          await axios.put(`${API_URL}/wholesalers/orders/${orderId}/status`, {
            status: 'cancelled',
            reason: bulkCancelReason.trim()
          }, { headers });
          succeeded++;
        } catch {
          failed++;
        }
      }

      toast.success(t('orders.bulk.cancelResult', { succeeded, failed }) || `${succeeded} cancelled, ${failed} failed`);
      clearSelection();
      fetchOrders();
    } catch (err) {
      console.error('Bulk cancel error:', err);
      toast.error(t('orders.bulk.error') || 'Failed to cancel orders');
    } finally {
      setBulkProcessing(false);
      setBulkCancelReason('');
    }
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) newSet.delete(orderId);
      else newSet.add(orderId);
      return newSet;
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      case 'confirmed':
      case 'processing': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'shipped': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getOrderCardBg = (status) => {
    switch (status) {
      case 'delivered': return 'bg-green-50 border-green-300 hover:border-green-400';
      case 'cancelled': return 'bg-red-50 border-red-300 hover:border-red-400';
      case 'confirmed':
      case 'processing': return 'bg-blue-50 border-blue-300 hover:border-blue-400';
      case 'shipped': return 'bg-purple-50 border-purple-300 hover:border-purple-400';
      case 'pending': return 'bg-yellow-50 border-yellow-300 hover:border-yellow-400';
      default: return 'bg-white border-gray-200 hover:border-gray-300';
    }
  };

  const getOrderStats = () => {
    return {
      total: statusCounts.all || 0,
      pending: statusCounts.pending || 0,
      processing: (statusCounts.processing || 0) + (statusCounts.confirmed || 0),
      totalRevenue: Array.isArray(orders) ? orders.reduce((sum, o) => sum + (o.totals?.total || 0), 0) : 0
    };
  };

  const stats = getOrderStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex justify-center items-center h-64">
          <div className="text-xl text-gray-600">{t('orders.loading')}</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="container mx-auto px-4 py-8 pt-16 lg:pt-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">{t('orders.pageTitle')}</h1>
          <p className="text-gray-600">{t('orders.pageSubtitle')}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-sm text-gray-600 mb-1">{t('orders.stats.totalOrders')}</div>
            <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-yellow-50 rounded-lg shadow-md p-6 border border-yellow-200">
            <div className="text-sm text-yellow-800 mb-1">{t('orders.stats.pending')}</div>
            <div className="text-3xl font-bold text-yellow-900">{stats.pending}</div>
          </div>
          <div className="bg-blue-50 rounded-lg shadow-md p-6 border border-blue-200">
            <div className="text-sm text-blue-800 mb-1">{t('orders.stats.processing')}</div>
            <div className="text-3xl font-bold text-blue-900">{stats.processing}</div>
          </div>
          <div className="bg-green-50 rounded-lg shadow-md p-6 border border-green-200">
            <div className="text-sm text-green-800 mb-1">{t('orders.stats.totalRevenue')}</div>
            <div className="text-3xl font-bold text-green-900">€{stats.totalRevenue.toFixed(2)}</div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Filter Controls */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <input
              type="text"
              placeholder={t('orders.search.placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">{t('orders.filters.allStatuses')} ({statusCounts.all})</option>
              <option value="pending">{t('orders.status.pending')} ({statusCounts.pending})</option>
              <option value="confirmed">{t('orders.status.confirmed')} ({statusCounts.confirmed})</option>
              <option value="processing">{t('orders.status.processing')} ({statusCounts.processing})</option>
              <option value="shipped">{t('orders.status.shipped')} ({statusCounts.shipped})</option>
              <option value="delivered">{t('orders.status.delivered')} ({statusCounts.delivered})</option>
              <option value="cancelled">{t('orders.status.cancelled')} ({statusCounts.cancelled})</option>
            </select>

            <select
              value={businessFilter}
              onChange={(e) => setBusinessFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">{t('orders.filters.allBusinesses')}</option>
              {businesses.map(business => (
                <option key={business._id} value={business._id}>
                  {business.businessName}
                </option>
              ))}
            </select>

            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">{t('orders.filters.allTime')}</option>
              <option value="today">{t('orders.filters.today')}</option>
              <option value="week">{t('orders.filters.thisWeek')}</option>
              <option value="month">{t('orders.filters.thisMonth')}</option>
            </select>
          </div>

          {(statusFilter !== 'all' || businessFilter !== 'all' || dateFilter !== 'all' || searchTerm) && (
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {t('orders.filters.clearFilters')}
            </button>
          )}

          <div className="text-sm text-gray-600 mt-2">
            {t('orders.filters.showing', { count: filteredOrders.length, total: statusCounts.all })}
          </div>
        </div>

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 text-lg">{t('orders.noOrders')}</p>
          </div>
        ) : (
          <div className="space-y-4" {...dragProps}>
            {filteredOrders.map((order) => (
              <div
                key={order._id}
                data-select-id={order._id}
                className={`rounded-lg shadow-md border-2 transition-all ${getOrderCardBg(order.status)} ${selectedIds.has(order._id) || dragSelectedIds.has(order._id) ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
              >
                <div
                  className="p-4 sm:p-6 cursor-pointer"
                  onClick={() => toggleOrderExpansion(order._id)}
                >
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3 sm:gap-4">
                      {/* Bulk Selection Checkbox */}
                      {order.status !== 'delivered' && order.status !== 'cancelled' && (() => {
                        const mismatchStatus = selectedIds.size > 0 && bulkSelectedStatus !== order.status;
                        const mismatchType = selectedIds.size > 0 && !selectedIds.has(order._id) && bulkSelectedIsSimple !== !!order.isSimple;
                        const isLocked = mismatchStatus || mismatchType;
                        return (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleOrderSelection(order._id, order.status);
                            }}
                            className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                              selectedIds.has(order._id)
                                ? 'bg-blue-600 border-blue-600 text-white'
                                : isLocked
                                  ? 'border-gray-200 text-transparent cursor-not-allowed opacity-30'
                                  : 'border-gray-300 hover:border-blue-400 text-transparent hover:bg-blue-50'
                            }`}
                            disabled={isLocked}
                            title={
                              mismatchStatus
                                ? t('orders.bulk.sameStatusOnly', 'Only orders with the same status can be selected together')
                                : mismatchType
                                  ? t('orders.bulk.sameTypeOnly', 'Mix simple and full orders separately')
                                  : ''
                            }
                          >
                            {selectedIds.has(order._id) && <CheckSquare className="w-4 h-4" />}
                          </button>
                        );
                      })()}

                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-bold text-gray-900">
                            {t('orders.details.orderNumber')}{order.orderNumber}
                          </h3>
                          {!order.isSimple && order.modificationHistory && order.modificationHistory.length > 0 && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">{t('orders.details.modified')}</span>
                          )}
                          {order.isSimple && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-800 border border-sky-200">
                              {t('orders.simpleOrders.badge', 'Simple')}
                            </span>
                          )}
                          {order.placedOnBehalf?.isOnBehalf && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                              {t('orders.details.placedBy', { name: order.placedOnBehalf.placedByEmail?.split('@')[0] || 'Team' })}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          {order.businessId?.businessName || 'Unknown Business'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {format(new Date(order.createdAt), 'dd/MM/yy HH:mm')}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(order.status)}`}>
                        {t(`orders.statusLabels.${order.status}`, order.status.charAt(0).toUpperCase() + order.status.slice(1))}
                      </span>

                      <div className="text-right">
                        <p className="text-sm text-gray-600">{t('orders.totals.orderTotal')}</p>
                        <p className="text-xl font-bold text-gray-900">
                          €{order.totals?.total?.toFixed(2) || '0.00'}
                        </p>
                      </div>

                      {expandedOrders.has(order._id) ? (
                        <ChevronUp className="w-6 h-6 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedOrders.has(order._id) && (
                  <div className="border-t border-gray-200 p-6 bg-white bg-opacity-50">
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">{t('orders.orderStatus', 'Κατάσταση Παραγγελίας')}</h4>
                      <OrderStatusProgression
                        currentStatus={order.status}
                        onStatusChange={(newStatus, payload) => handleStatusUpdate(order._id, newStatus, payload)}
                        disabled={!canManageStatus}
                        isSimple={order.isSimple}
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3 mb-6">
                      {canManageStatus && !order.isSimple && order.status === 'confirmed' && (
                        <button
                          onClick={() => handleModifyOrder(order)}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          <Edit className="w-4 h-4" />
                          {t('orders.details.modifyOrder', 'Τροποποίηση Παραγγελίας')}
                        </button>
                      )}

                      {canManageStatus && order.isSimple && order.status === 'pending' && (
                        <button
                          onClick={() => handleLightEditOrder(order)}
                          className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors text-sm font-medium"
                        >
                          <Edit className="w-4 h-4" />
                          {t('orders.simpleOrders.editItems', 'Edit Items')}
                        </button>
                      )}

                      {canManageStatus && ['pending', 'confirmed'].includes(order.status) && (
                        <button
                          onClick={() => handleApplyDiscount(order)}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                        >
                          <Percent className="w-4 h-4" />
                          {order.discount?.type !== 'none' && order.discount?.percentage > 0
                            ? t('orders.editDiscount', 'Επεξεργασία Έκπτωσης') + ` (${order.discount.percentage}%)`
                            : t('orders.applyDiscount', 'Εφαρμογή Έκπτωσης')
                          }
                        </button>
                      )}

                      {canManageStatus && (
                        order.isSimple
                          ? ['pending', 'delivered'].includes(order.status)
                          : ['processing', 'shipped', 'delivered'].includes(order.status)
                      ) && (
                        <button
                          onClick={() => handleUploadInvoice(order)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                        >
                          <Upload className="w-4 h-4" />
                          {order.invoiceUrl ? t('orders.reuploadInvoice', 'Αντικατάσταση Τιμολογίου') : t('orders.details.uploadInvoice', 'Ανέβασμα Τιμολογίου')}
                        </button>
                      )}

                      {order.invoiceUrl && (
                        <button onClick={() => handleViewInvoice(order._id)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg">
                          📄 {t('orders.viewInvoice', 'Προβολή Τιμολογίου')}
                        </button>
                      )}

                      {/* Export to Excel (always available) */}
                      <button
                        onClick={() => handleExportERP(order)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                      >
                        <Download className="w-4 h-4" />
                        {t('orders.exportToExcel', 'Export Excel')}
                      </button>

                      {/* ERP Sync Status Badge + Retry */}
                      {order.erpMetadata?.erpSyncStatus && order.erpMetadata.erpSyncStatus !== 'not_applicable' && (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          order.erpMetadata.erpSyncStatus === 'synced' ? 'bg-green-100 text-green-800' :
                          order.erpMetadata.erpSyncStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`} title={order.erpMetadata.erpError || ''}>
                          ERP: {t(`erp.syncStatus.${order.erpMetadata.erpSyncStatus}`, order.erpMetadata.erpSyncStatus)}
                        </span>
                      )}
                      {order.erpMetadata?.erpSyncStatus === 'failed' && (
                        <button
                          onClick={async () => {
                            try {
                              const res = await ordersAPI.retryErpSync(order._id);
                              if (res.data.success) {
                                fetchOrders();
                              }
                            } catch {}
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                        >
                          <FileDown className="w-4 h-4" />
                          {t('erp.retry', 'Retry ERP')}
                        </button>
                      )}
                    </div>

                    {!order.isSimple && <ModificationHistorySection order={order} />}
                    {order.isSimple && order.lightEditAudit?.length > 0 && (
                      <LightEditAuditSection entries={order.lightEditAudit} />
                    )}

                    {/* Items List */}
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        📦 {order.items?.length || 0} {t('orders.items.title', 'products')}
                      </h4>
                      <div className="space-y-2">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className={`flex justify-between items-center py-2 border-b border-gray-100 last:border-0 ${item.status === 'removed' ? 'opacity-50' : ''}`}>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-800">{item.name}</span>
                                {!order.isSimple && item.status && item.status !== 'active' && (
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                    item.status === 'modified' ? 'bg-orange-100 text-orange-800' :
                                    item.status === 'out_of_stock' ? 'bg-red-100 text-red-800' :
                                    item.status === 'removed' ? 'bg-gray-100 text-gray-600' :
                                    'bg-green-100 text-green-800'
                                  }`}>
                                    {t(`orders.itemStatus.${item.status}`, item.status)}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                {item.quantity} × €{(item.unitPrice || 0).toFixed(2)}
                                {item.originalQuantity && item.originalQuantity !== item.quantity && (
                                  <span className="text-orange-500 ml-2">({t('orders.items.originalQty', 'was')}: {item.originalQuantity})</span>
                                )}
                              </div>
                              {item.modificationNote && (
                                <p className="text-xs text-orange-600 mt-0.5">{item.modificationNote}</p>
                              )}
                            </div>
                            <div className="font-semibold text-gray-900">
                              €{(item.totalPrice || (item.quantity * (item.unitPrice || 0))).toFixed(2)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Order Totals */}
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{t('orders.totals.subtotal')}:</span>
                        <span className="font-medium">€{order.totals?.subtotal?.toFixed(2) || '0.00'}</span>
                      </div>

                      {/* Catalog Discount */}
                      {order.catalogDiscount?.percentage > 0 && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-blue-600 font-medium">{t('orders.totals.catalogDiscount', 'Catalog Discount')} ({order.catalogDiscount.percentage}%):</span>
                            <span className="font-semibold text-blue-600">-€{order.catalogDiscount.amount?.toFixed(2) || order.totals?.catalogDiscount?.toFixed(2) || '0.00'}</span>
                          </div>
                          {order.catalogDiscount.names?.length > 0 && (
                            <div className="text-xs text-blue-500 ml-1">{order.catalogDiscount.names.join(', ')}</div>
                          )}
                        </>
                      )}

                      {/* Customer Discount */}
                      {order.discount?.type !== 'none' && order.discount?.percentage > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-green-600 font-medium">{t('orders.totals.customerDiscount', 'Customer Discount')} ({order.discount.percentage}%):</span>
                          <span className="font-semibold text-green-600">-€{order.discount.amount?.toFixed(2) || '0.00'}</span>
                        </div>
                      )}

                      {/* After All Discounts */}
                      {(order.catalogDiscount?.percentage > 0 || (order.discount?.type !== 'none' && order.discount?.percentage > 0)) && (
                        <div className="flex justify-between text-sm border-t border-gray-200 pt-2">
                          <span className="text-gray-600">{t('orders.totals.afterDiscount')}:</span>
                          <span className="font-medium">€{order.totals?.afterDiscount?.toFixed(2) || '0.00'}</span>
                        </div>
                      )}

                      {order.totals?.deliveryFee > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">{t('orders.totals.deliveryFee')}</span>
                          <span className="font-medium">€{order.totals.deliveryFee.toFixed(2)}</span>
                        </div>
                      )}

                      <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                        <span>{t('orders.totals.totalNet')}:</span>
                        <span className="text-blue-600">€{order.totals?.total?.toFixed(2) || '0.00'}</span>
                      </div>
                      <p className="text-xs text-gray-500 italic mt-2">{t('orders.totals.vatDisclaimer')}</p>
                    </div>

                    {/* Delivery Info */}
                    {order.delivery && (
                    <div className="bg-blue-50 rounded-lg p-4 mb-6">
                      <h4 className="font-semibold text-gray-800 mb-3">{t('orders.delivery.title')}</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('orders.delivery.type')}:</span>
                          <span className="font-medium capitalize">{order.delivery?.type || t('orders.delivery.na')}</span>
                        </div>
                        {order.delivery?.scheduledFor && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">{t('orders.delivery.scheduledDate')}:</span>
                            <span className="font-medium">{format(new Date(order.delivery.scheduledFor), 'dd/MM/yy')}</span>
                          </div>
                        )}
                        <div className="pt-2 border-t border-blue-100">
                          <ConfirmDeliveryDate
                            order={order}
                            mode="wholesaler"
                            onChange={(updated) => {
                              setOrders(prev => prev.map(o => o._id === updated._id ? { ...o, ...updated } : o));
                            }}
                          />
                        </div>
                        {order.delivery?.address && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">{t('orders.delivery.address')}:</span>
                            <span className="font-medium text-right ml-4">{order.delivery.address}</span>
                          </div>
                        )}
                        {order.delivery?.instructions && (
                          <div className="flex flex-col">
                            <span className="text-gray-600 mb-1">{t('orders.delivery.instructions')}:</span>
                            <span className="font-medium">{order.delivery.instructions}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    )}

                    {/* Cancellation Reason */}
                    {order.status === 'cancelled' && order.cancellationReason && (
                      <div className="border-t border-gray-200 pt-4 mb-4">
                        <h4 className="font-semibold text-gray-800 mb-3">{t('orders.cancellationReason', 'Cancellation Reason')}</h4>
                        <div className="bg-red-50 rounded-lg border border-red-200 p-4 text-sm">
                          <p className="text-red-800">{order.cancellationReason}</p>
                        </div>
                      </div>
                    )}

                    {/* Comments */}
                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5" />
                        {t('orders.comments.title')}
                      </h4>

                      {order.comments && order.comments.length > 0 && (
                        <div className="space-y-3 mb-4">
                          {order.comments.map((comment, idx) => (
                            <div
                              key={idx}
                              className={`p-3 rounded-lg ${
                                comment.author === 'wholesaler'
                                  ? 'bg-blue-50 border border-blue-200'
                                  : 'bg-gray-50 border border-gray-200'
                              }`}
                            >
                              <div className="flex justify-between items-start mb-1">
                                <span className="font-medium text-sm text-gray-900">
                                  {comment.authorName || (comment.author === 'wholesaler' ? t('orders.comments.you') : t('orders.comments.customer'))}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {comment.createdAt ? format(new Date(comment.createdAt), 'dd/MM/yy HH:mm') : t('orders.comments.justNow')}
                                </span>
                              </div>
                              <p className="text-gray-700 text-sm">{comment.message}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={commentText[order._id] || ''}
                          onChange={(e) => setCommentText({ ...commentText, [order._id]: e.target.value })}
                          placeholder={t('orders.comments.placeholder')}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleAddComment(order._id);
                          }}
                        />
                        <button
                          onClick={() => handleAddComment(order._id)}
                          disabled={!commentText[order._id]?.trim()}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                          {t('orders.comments.send', 'Send')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Load More Button */}
        {!loading && filteredOrders.length > 0 && hasMore && (
          <div className="mt-6 text-center">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium shadow-md hover:shadow-lg"
            >
              {loadingMore ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('orders.loadingMore', 'Loading more...')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {t('orders.loadMoreOrders', 'Load More Orders')}
                  {pagination && <span className="text-sm opacity-80">({orders.length} / {pagination.total})</span>}
                </span>
              )}
            </button>
            {pagination && (
              <p className="mt-3 text-sm text-gray-600">{t('orders.showingOf', {shown: orders.length, total: pagination.total})}</p>
            )}
          </div>
        )}

        {/* Modals */}
        {showModifyModal && selectedOrder && (
          <OrderModificationModal
            order={selectedOrder}
            onClose={() => { setShowModifyModal(false); setSelectedOrder(null); }}
            onSave={handleSaveModifications}
          />
        )}

        {showLightEditModal && selectedOrder && (
          <OrderLightEditModal
            order={selectedOrder}
            erpActive={erpActive}
            onClose={() => { setShowLightEditModal(false); setSelectedOrder(null); }}
            onSave={handleSaveLightEdit}
          />
        )}

        {showInvoiceModal && selectedOrder && (
          <InvoiceUploadModal
            order={selectedOrder}
            onClose={() => { setShowInvoiceModal(false); setSelectedOrder(null); }}
            onUpload={handleInvoiceUpload}
          />
        )}

        {showDiscountModal && selectedOrder && (
          <OrderDiscountModal
            order={selectedOrder}
            onClose={() => { setShowDiscountModal(false); setSelectedOrder(null); }}
            onDiscountApplied={handleDiscountApplied}
          />
        )}

        {/* Export Modal Rendering */}
        {showExportModal && (orderToExport || ordersToExport) && (
          <ExportSettingsModal
            isOpen={showExportModal}
            onClose={() => { setShowExportModal(false); setOrderToExport(null); setOrdersToExport(null); }}
            orderToExport={orderToExport}
            ordersToExport={ordersToExport}
          />
        )}

        {showCancelModal && selectedOrder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('orders.cancelModal.title', 'Ακύρωση Παραγγελίας')} {selectedOrder.orderNumber}</h3>
              <p className="text-sm text-gray-600 mb-4">{t('orders.cancelModal.description', 'Παρακαλώ δώστε τον λόγο ακύρωσης. Θα αποσταλεί στην επιχείρηση.')}</p>
              <textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder={t('orders.cancelModal.placeholder', 'Εισάγετε λόγο ακύρωσης (π.χ. Εξαντλημένο, Μη διαθέσιμο προϊόν, κλπ.)')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                rows="4"
                required
              />
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => { setShowCancelModal(false); setCancellationReason(''); setSelectedOrder(null); }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  {t('orders.cancelModal.close', 'Κλείσιμο')}
                </button>
                <button
                  onClick={handleConfirmCancellation}
                  disabled={!cancellationReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {t('orders.cancelModal.confirm', 'Επιβεβαίωση Ακύρωσης')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Confirm Modal for Bulk Advance ── */}
        <ConfirmModal
          isOpen={confirmModal.open && confirmModal.type === 'advance'}
          title={t('orders.bulk.confirmAdvanceTitle', 'Advance Orders')}
          message={t('orders.bulk.confirmAdvance', {
            count: selectedIds.size,
            from: t(`orders.statuses.${bulkSelectedStatus}`, bulkSelectedStatus),
            to: t(`orders.statuses.${bulkNextStatus}`, bulkNextStatus)
          }) || `Advance ${selectedIds.size} orders from ${bulkSelectedStatus} to ${bulkNextStatus}?`}
          icon="warning"
          confirmLabel={t('orders.bulk.advanceTo', 'Advance to') + ' ' + t(`orders.statuses.${bulkNextStatus}`, bulkNextStatus || '')}
          confirmColor="blue"
          onConfirm={confirmBulkAdvance}
          onCancel={() => setConfirmModal({ open: false, type: null })}
        />

        {/* ── Confirm Modal for Bulk Cancel (with textarea) ── */}
        <ConfirmModal
          isOpen={confirmModal.open && confirmModal.type === 'bulkCancel'}
          title={t('orders.bulk.cancelTitle', 'Cancel Orders')}
          message={t('orders.bulk.cancelPrompt', 'Enter cancellation reason for all selected orders:')}
          icon="danger"
          confirmLabel={t('orders.bulk.cancel', 'Cancel Orders')}
          confirmColor="red"
          onConfirm={confirmBulkCancel}
          onCancel={() => { setConfirmModal({ open: false, type: null }); setBulkCancelReason(''); }}
        >
          <textarea
            value={bulkCancelReason}
            onChange={(e) => setBulkCancelReason(e.target.value)}
            placeholder={t('orders.cancelModal.placeholder', 'Enter cancellation reason...')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-sm"
            rows="3"
            autoFocus
          />
        </ConfirmModal>

        {/* ── Floating Bulk Action Bar ── */}
        <FloatingActionBar count={selectedIds.size} onClear={clearSelection} onSelectAll={selectAllFiltered}>
          {bulkSelectedStatus && (
            <span className="text-xs text-gray-500">
              {t('orders.bulk.currentStatus', 'Current status')}: <span className="font-medium capitalize">{t(`orders.statuses.${bulkSelectedStatus}`, bulkSelectedStatus)}</span>
            </span>
          )}

          {/* Advance to next status — manager/office only */}
          {bulkNextStatus && canManageStatus && (
            <button
              onClick={handleBulkAdvance}
              disabled={bulkProcessing}
              className="flex items-center gap-2 px-4 sm:px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {bulkProcessing ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">
                {t('orders.bulk.advanceTo', 'Advance to')} {t(`orders.statuses.${bulkNextStatus}`, bulkNextStatus)}
              </span>
              <span className="sm:hidden capitalize">
                → {t(`orders.statuses.${bulkNextStatus}`, bulkNextStatus)}
              </span>
            </button>
          )}

          {/* Export selected to Excel */}
          <button
            onClick={() => {
              const selectedOrders = orders.filter(o => selectedIds.has(o._id));
              handleBulkExportExcel(selectedOrders);
            }}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium shadow-sm"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">{t('orders.bulk.exportExcel', 'Export Excel')}</span>
            <span className="sm:hidden">Excel</span>
          </button>

          {/* Cancel selected (only for pending/confirmed) — manager/office only */}
          {bulkSelectedStatus && canManageStatus && ['pending', 'confirmed'].includes(bulkSelectedStatus) && (
            <button
              onClick={handleBulkCancel}
              disabled={bulkProcessing}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50 shadow-sm"
            >
              <XIcon className="w-4 h-4" />
              <span className="hidden sm:inline">{t('orders.bulk.cancel', 'Cancel Orders')}</span>
              <span className="sm:hidden">{t('common.cancel', 'Cancel')}</span>
            </button>
          )}
        </FloatingActionBar>

        {/* Drag Select Overlay */}
        <DragSelectOverlay isDragging={isDragging} dragRect={dragRect} />
      </main>
      <Footer />
    </div>
  );
};

export default WholesalerOrders;
